import { ProvisioningStateData } from "components/ProvisioningStateDot/ProvisioningStateDot";
import { FeConfigType } from "config/config";
import { BundleState } from "containers/BundleManager/BundleManager";
import { UncommittedDataGroupedByName } from "containers/Resources/screens/ResourcesSingle/components/GitAction/commitActions/utils";
import { Scheduler } from "graphql/generated/graphql";
import { History, LocationState } from "history";
import { MdiReactIconComponentType } from "mdi-react";
import { PropsWithChildren, ReactElement, ReactNode } from "react";
import { Dispatch } from "redux";

export type ActionData = {
    resourceType: FeConfigType;
    resourceData: any;
    /** Redux cache key for the open record page (may use URL name, not record id). */
    iri?: string;
    activeProjectId: string;
    userData: any;
    isReadOnly: boolean;
    afterAction?: () => void | null;
    isHidden?: boolean;
    uncommittedData: UncommittedDataGroupedByName;
    setDirtyForm?: () => void;
    /**
     * Re-seed the open record editors from the server after a "Migrate
     * deprecations" apply: discard the stale pre-migration editor buffer and
     * show the migrated content, mirroring the manual-Refresh path. The migrate
     * saga changes the record on the SERVER, so the editors must be force-updated
     * (a dirty flag alone leaves them stale until a page refresh).
     * See operator-task-fe-migrate-deprecations-code-editor-no-refresh.
     */
    onMigrateDeprecationsApplied?: () => void;
    currentBundleState?: BundleState;
    remoteBundleVersion?: string;
    saveAdditionalParams?: { commit?: boolean; migrate?: boolean };
    schedulerList?: Scheduler[];
    canUserCreateUser?: boolean;
    isGitLocked?: boolean;
    provisioningState?: ProvisioningStateData;
    bootstrapUserName?: string;
};

/**
 * Async handler for a record action. The returned promise must always settle
 * (resolve or reject) — including when the user dismisses a modal without
 * applying the action (resolve with null is fine).
 */
export type Action = (
    data: ActionData,
    dispatch: Dispatch,
    history: History<LocationState>,
    additionalParams?: any
) => Promise<any>;

export type ActionSignature = {
    key: string;
    action: Action;
    label: (data: ActionData) => string;
    icon: (data: ActionData) => MdiReactIconComponentType;
    inlineActionIcon?: (data: ActionData) => MdiReactIconComponentType;
    image?: (data: ActionData) => ReactNode;
    omitWhen?: (data: ActionData) => boolean;
    disableWhen?: (data: ActionData) => boolean;
    /**
     * For an action that only goes to an addressable place: its URL. The
     * action then renders as a real link, so middle-click / Ctrl-click open it
     * in a new tab and right-click offers "copy link address". A plain click
     * navigates in place. When it returns no URL, the button runs `action`.
     */
    href?: (data: ActionData) => string | undefined;
    submitFormOnAction?: boolean;
    isDropdown?: (data: ActionData) => boolean;
    dropdownContent?: ({
        actionData,
        dispatch,
        history,
        isDisabled,
        saveForm,
    }: {
        actionData: ActionData;
        dispatch: Dispatch;
        history: History;
        isDisabled: boolean;
        saveForm: () => Promise<any>;
    }) => any;
    dropdownIcon?: any;
    dropdownLabel?: string;
    hasAfterAction?: boolean;
    tooltipOverlay?: (data: ActionData) => string;
    tooltipComponent?: (props: PropsWithChildren<{}>) => ReactElement;
    hasDropdownContentAfterAction?: boolean;
};

/**
 * The URL a shown action links to, or undefined when it renders as a button:
 * no `href`, no URL for this record, or the action is disabled.
 */
export const getActionHref = (
    action: Pick<ActionSignature, "href" | "disableWhen">,
    data: ActionData
): string | undefined => {
    const href = action.href?.(data);
    if (!href || action.disableWhen?.(data)) return undefined;
    return href;
};
